/**
 * Tiny wrapper around the browser's Web Speech API
 * (`SpeechRecognition` / `webkitSpeechRecognition`). Provides the same
 * onPartial / onFinal / onError surface as `useQwenAsr` so EmbedViewer
 * can swap providers behind a single hook.
 *
 * Why this over the server-side ASR relay:
 *   - The relay's stateful WebSocket model doesn't fit Lambda's
 *     per-request lifecycle (sessions get lost across containers).
 *   - The relay's upstream binding is hard-coded to Qwen3 WS protocol;
 *     OpenAI's gpt-4o-transcribe needs a different shape entirely.
 *   - Browser-native recognition runs entirely on the user's device:
 *     no upstream cost, no API key, no session state to share.
 *
 * Coverage:
 *   - Chrome desktop + Android: full support including zh-TW.
 *   - Edge: full support.
 *   - Safari: partial; works but the continuous mode is flakier.
 *   - Firefox: no support (returns isSupported=false; caller should
 *     show a "請使用 Chrome / Edge" hint).
 *
 * Lifecycle: the hook holds at most ONE recognition instance. start()
 * is idempotent — calling while already listening is a no-op.
 * stop() cancels the in-flight session and resets state to idle.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type BrowserSpeechState = 'idle' | 'listening' | 'finalising' | 'error';

export interface UseBrowserSpeechOptions {
  /** zh-TW / en-US / etc. Defaults to navigator.language.
   *  Per voice-loop iter 22 — may be a getter function for dynamic
   *  per-utterance language. The getter is invoked at every start()
   *  so stop()/start() picks up the latest value without remounting
   *  the hook. */
  lang?: string | (() => string);
  /**
   * Per voice-loop iter 11 — Chrome's SpeechRecognition surfaces a
   * `confidence` (0..1) on each result.alternative. Partial events
   * usually carry confidence=0 (the engine is still uncertain), so
   * the per-call gate should treat absent / 0 confidence as "unknown"
   * not "bad". Final events carry meaningful values, ~0.7-0.95 for
   * clean utterances and 0.1-0.4 for ambiguous syllables / noise.
   */
  onPartial?: (text: string, confidence: number) => void;
  onFinal?: (text: string, confidence: number) => void;
  onError?: (message: string) => void;
  /**
   * Per voice-loop iter 8 — fires the moment SpeechRecognition's
   * built-in voice-activity detector hears the user start speaking.
   * Lands ~100-300ms before the first partial transcript arrives;
   * EmbedRuntime uses this to pause TTS playback fast for the
   * "phone-call interrupt" feel. May fire spuriously on AI audio
   * bleed; the caller should speculatively resume if no partial
   * follows within ~800ms.
   */
  onSpeechStart?: () => void;
  /**
   * Per `embed-mid-reply-interrupt`. Default `'turn-taking'` keeps
   * the existing behaviour: one utterance per start() call,
   * recogniser auto-stops on silence so the caller can drive the
   * turn cycle. `'continuous'` keeps the recogniser hot through
   * silence — final segments emit as the user pauses, but start()
   * stays armed. The interrupt detector reads partial events to
   * reset the idle timer; final events drive the next turn.
   */
  mode?: 'turn-taking' | 'continuous';
}

export interface UseBrowserSpeechReturn {
  state: BrowserSpeechState;
  errorMessage: string | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  /**
   * False when the host browser has no SpeechRecognition implementation.
   * Caller should fall back to the server relay or show an error.
   */
  isSupported: boolean;
}

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((this: SpeechRecognitionLike, ev: SpeechRecognitionResultEventLike) => unknown) | null;
  onend: ((this: SpeechRecognitionLike, ev: Event) => unknown) | null;
  onerror: ((this: SpeechRecognitionLike, ev: { error: string; message?: string }) => unknown) | null;
  onspeechstart: ((this: SpeechRecognitionLike, ev: Event) => unknown) | null;
}

interface SpeechRecognitionResultEventLike extends Event {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    length: number;
    [index: number]: { transcript: string; confidence: number };
  }>;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useBrowserSpeech(opts: UseBrowserSpeechOptions = {}): UseBrowserSpeechReturn {
  const { lang, onPartial, onFinal, onError, onSpeechStart, mode = 'turn-taking' } = opts;
  const [state, setState] = useState<BrowserSpeechState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const Ctor = getSpeechRecognitionCtor();
  const isSupported = Ctor !== null;
  // The latest interim transcript — used as a fallback final value
  // when the browser fires `end` without an explicit final result.
  const lastPartialRef = useRef('');

  const reportError = useCallback((message: string) => {
    setErrorMessage(message);
    setState('error');
    onError?.(message);
  }, [onError]);

  const cleanup = useCallback((): void => {
    const r = recognitionRef.current;
    if (!r) return;
    try { r.onresult = null; r.onend = null; r.onerror = null; r.onspeechstart = null; } catch { /* noop */ }
    try { r.abort(); } catch { /* noop */ }
    recognitionRef.current = null;
  }, []);

  const start = useCallback(async (): Promise<void> => {
    console.log('[browser-speech] start() called', { hasCtor: !!Ctor, state, hasRecognition: !!recognitionRef.current });
    if (!Ctor) {
      reportError('此瀏覽器不支援語音辨識,請改用 Chrome 或 Edge');
      return;
    }
    if (state === 'listening' || recognitionRef.current) {
      console.warn('[browser-speech] start() early return — already running', { state });
      return;
    }
    setErrorMessage(null);
    lastPartialRef.current = '';

    const r = new Ctor();
    const resolvedLang = typeof lang === 'function' ? lang() : lang;
    r.lang = resolvedLang ?? navigator.language ?? 'zh-TW';
    // Continuous mode keeps the recogniser running through silence
    // (per `embed-mid-reply-interrupt` spec) — final segments still
    // emit as the user pauses, but start() stays armed; no need to
    // re-call start() between turns. Turn-taking mode (default)
    // matches Chrome's usual one-utterance-per-start behaviour.
    r.continuous = mode === 'continuous';
    r.interimResults = true;
    r.maxAlternatives = 1;

    r.onresult = (ev) => {
      let interim = '';
      let finalText = '';
      // Track the most recent alternative's confidence per chunk
      // type. Multiple results in one event get the latest value
      // (good enough — partial events typically fire one result at
      // a time anyway).
      let interimConfidence = 0;
      let finalConfidence = 0;
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        if (!result || result.length === 0) continue;
        const alt = result[0];
        if (!alt) continue;
        if (result.isFinal) {
          finalText += alt.transcript;
          finalConfidence = alt.confidence;
        } else {
          interim += alt.transcript;
          interimConfidence = alt.confidence;
        }
      }
      if (interim) {
        lastPartialRef.current = interim;
        onPartial?.(interim, interimConfidence);
      }
      if (finalText) {
        lastPartialRef.current = '';
        onFinal?.(finalText.trim(), finalConfidence);
        // In continuous mode the recognition object KEEPS RUNNING
        // after emitting a final — there's no "finalising" phase,
        // it's already listening for the next utterance. Forcing
        // state='finalising' here desynced the externally-visible
        // state from reality and made the re-arm path think ASR
        // was busy when it was actually still listening fine.
        // For non-continuous (one-shot) mode keep the original
        // semantics — there onend will fire shortly after.
        if (mode !== 'continuous') setState('finalising');
      }
    };

    r.onend = () => {
      // If the browser ended without firing a final result (common
      // when the user pauses briefly), fall back to the latest
      // interim text.
      if (lastPartialRef.current) {
        const t = lastPartialRef.current.trim();
        lastPartialRef.current = '';
        // No confidence available on the fallback path — partial
        // confidence is usually 0 anyway. Pass 0 and let the
        // consumer's gate treat it as "unknown".
        if (t) onFinal?.(t, 0);
      }
      cleanup();
      setState((s) => (s === 'error' ? s : 'idle'));
    };

    r.onspeechstart = () => {
      // Browser-detected voice activity. Fires ahead of the first
      // partial transcript by ~100-300ms. May be a false positive
      // (mic noise / AI bleed) — caller must guard with their own
      // resume-on-silence policy.
      onSpeechStart?.();
    };

    r.onerror = (ev) => {
      const code = ev.error;
      // 'no-speech' / 'aborted' aren't really errors — user just
      // didn't say anything, or stop() was called. Don't escalate.
      if (code === 'no-speech' || code === 'aborted') {
        cleanup();
        setState('idle');
        return;
      }
      const friendly = code === 'not-allowed'
        ? '麥克風權限被拒,請在瀏覽器設定中允許'
        : code === 'audio-capture'
          ? '找不到麥克風裝置'
          : code === 'network'
            ? '網路錯誤,請檢查連線'
            : ev.message ?? code;
      reportError(`SpeechRecognition: ${friendly}`);
      cleanup();
    };

    try {
      r.start();
      recognitionRef.current = r;
      setState('listening');
      console.log('[browser-speech] recognition.start() ok — state=listening');
    } catch (e) {
      console.warn('[browser-speech] recognition.start() threw', e);
      reportError(`SpeechRecognition start 失敗: ${(e as Error).message ?? e}`);
      cleanup();
    }
  }, [Ctor, state, lang, mode, onPartial, onFinal, onSpeechStart, reportError, cleanup]);

  const stop = useCallback(async (): Promise<void> => {
    const r = recognitionRef.current;
    if (!r) return;
    setState('finalising');
    try { r.stop(); } catch { /* noop — onend handles state */ }
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => { cleanup(); };
  }, [cleanup]);

  return { state, errorMessage, start, stop, isSupported };
}
